import { useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { toast } from 'keep-react'
import { useDispatch, useSelector } from 'react-redux'
import { useRegisterCustomerMutation } from '@store/api-helpers/createCustomerApi'
import { setCredentials } from '@store/features/authSlice'
import { RegistrationData } from '@models/auth.model'
import { createRegisterSchema, RegisterFormData } from '../schema'
import { selectBrandId } from '@store/features/styleSchemaSlice'
import { useCookieConsent } from '@hooks/useCookieConsent'
import { getCountryByCode } from '@/lib/constants/countries'

/**
 * Custom hook for managing registration form state and logic.
 * 
 * Handles:
 * - Form validation using React Hook Form with Zod schema
 * - Registration API calls through RTK Query
 * - Post-registration contact synchronization
 * - Retry promotions activation after successful registration
 * - Multi-brand support and internationalization
 * 
 * @returns {Object} Registration form utilities and state
 * @returns {Object} returns.control - React Hook Form control object
 * @returns {Function} returns.handleSubmit - Form submission handler
 * @returns {Function} returns.onSubmit - Form data processing function
 * @returns {Object} returns.errors - Form validation errors
 * @returns {boolean} returns.isLoading - Registration API loading state
 * @returns {boolean} returns.showSuccessMessage - Success message display flag
 * @returns {boolean} returns.allFieldsEmpty - All required fields empty flag
 * @returns {boolean} returns.privacyAccepted - Privacy policy acceptance state
 * @returns {boolean} returns.marketingAccepted - Marketing consent state
 * @returns {boolean} returns.showPassword - Password visibility toggle state
 * @returns {boolean} returns.showConfirmPassword - Confirm password visibility state
 * @returns {Function} returns.togglePasswordVisibility - Password visibility toggle function
 * @returns {Function} returns.toggleConfirmPasswordVisibility - Confirm password visibility toggle function
 * @returns {Function} returns.handleTermsToggle - Marketing terms toggle handler
 * @returns {Function} returns.handlePrivacyToggle - Privacy policy toggle handler
 * @returns {boolean} returns.isFormValid - Overall form validation state
 */
export const useRegisterForm = () => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const brandId = useSelector(selectBrandId)
  const { cookiesAccepted } = useCookieConsent()
  // Zustandsvariablen
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [allFieldsEmpty, setAllFieldsEmpty] = useState(true)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)

  const [registerCustomer, { isLoading, isSuccess, data: registrationData }] =
    useRegisterCustomerMutation()

  // Schema mit aktuellen Übersetzungen
  const schema = useMemo(() => createRegisterSchema(t), [t])

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitted, isValid },

    trigger,
    watch,
    setValue,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      salutation: t('register.defaults.salutation'),
      country: t('register.defaults.country'),
      marketingAccepted: true,
      privacyAccepted: false,
    },
    mode: 'onChange', // Ändern zu onChange für sofortige Validierung
    criteriaMode: 'all',
  })

  // Diese Werte aus dem Formular-State beziehen, nicht aus separatem React-State
  const marketingAccepted = watch('marketingAccepted')
  const privacyAccepted = watch('privacyAccepted')
  const watchedFields = watch()

  // Überwache Formgültigkeit
  useEffect(() => {
    setIsFormValid(isValid)
  }, [isValid])

  // Überprüfe, ob alle Felder ausgefüllt sind
  useEffect(() => {
    const requiredFields = [
      'salutation',
      'firstName',
      'lastName',
      'email',
      'emailConfirm',
      'streetAndHouseNumber',
      'postalCode',
      'city',
      'password',
      'passwordConfirm',
    ]

    const allFieldsFilled =
      !requiredFields.some((field) => {
        const value = watchedFields[field as keyof RegisterFormData]
        return value === undefined || value === null || value === ''
      }) && watchedFields.dateOfBirth !== undefined

    // Formular ist nur gültig, wenn isValid UND alle Felder ausgefüllt sind
    setIsFormValid(isValid && allFieldsFilled)
    setAllFieldsEmpty(!allFieldsFilled)
  }, [isValid, watchedFields])
  // Revalidierung nach Sprachänderung
  useEffect(() => {
    if (isSubmitted) {
      trigger()
    }
  }, [isSubmitted, trigger, i18n.language])

  /**
   * Effect that handles successful registration workflow.
   * 
   * Executed when registration is successful and registration data is available.
   * Performs the following steps in sequence:
   * 1. Sets user credentials in Redux store
   * 2. Synchronizes contact with external system
   * 3. Activates retry promotions for the user
   * 4. Shows success message
   * 
   * @dependency isSuccess - Registration success state from RTK Query
   * @dependency registrationData - Registration response data containing user_id and jwt
   * @dependency dispatch - Redux dispatch function
   * @dependency cookiesAccepted - Cookie consent state
   */
  useEffect(() => {
    if (isSuccess && registrationData?.data) {
      const token = localStorage.getItem('auth_token')
      const userId = registrationData.data.user_id

      if (token) {
        dispatch(
          setCredentials({
            jwt: token,
            user_id: userId || '',
            rememberMe: true,
            cookiesAccepted,
          })
        )

        // Kontakt synchronisieren und dann Retry Promotions aktivieren
        if (userId) {
          // Kontakt synchronisieren
          fetch('/api/khg/sync/createContact', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: token,
            },
            body: JSON.stringify({ id: userId }),
          })
            .then((response) => {
              if (!response.ok) {
                console.error(
                  'Fehler bei der Kontaktsynchronisierung:',
                  response.statusText
                )
              }
              return response.json()
            })
            .then(() => {
              // Retry Promotions aktivieren nach erfolgreicher Registrierung
              return fetch(`/api/data/users/${userId}`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: token,
                },
                body: JSON.stringify({
                  fields: {
                    retry_promotions: true
                  }
                }),
              })
            })
            .then((response) => {
              if (!response.ok) {
                console.error(
                  'Fehler beim Aktivieren der Retry Promotions:',
                  response.statusText
                )
              }
              return response.json()
            })
            .then(() => {
              // Retry Promotions successfully activated
            })
            .catch((error) =>
              console.error('Fehler bei Kontaktsynchronisierung oder Retry Promotions:', error)
            )
        }
      }

      setShowSuccessMessage(true)
    }
  }, [isSuccess, registrationData, dispatch, cookiesAccepted])

  /**
   * Processes form submission and registration data.
   * 
   * Transforms form data into the format expected by the registration API,
   * handles country code conversion, and initiates the registration process.
   * Includes comprehensive error handling for email conflicts and other failures.
   * 
   * @param {RegisterFormData} data - Validated form data from React Hook Form
   * @throws {Error} Displays toast notifications for various error scenarios
   * @returns {Promise<void>} Resolves when registration process completes
   */
  const onSubmit = async (data: RegisterFormData) => {
    try {
      // Transformiere Country-Code zu deutschem Namen
      const countryName = getCountryByCode(data.country)?.nameDE || data.country;
      
      const registrationData: RegistrationData = {
        email: data.email,
        password: data.password,
        anrede: data.salutation === t('register.salutations.mr') ? '1' : '2',
        vorname: data.firstName,
        nachname: data.lastName,
        telefon: data.telefon || '',
        streetAndHouseNumber: data.streetAndHouseNumber,
        adresszusatz: '',
        plz: data.postalCode,
        ort: data.city,
        country: countryName,
        geburtsdatum: data.dateOfBirth
          ? format(data.dateOfBirth, 'yyyy-MM-dd')
          : '',
        marketing: data.marketingAccepted ? 1 : 0, // Benutze data.marketingAccepted
        erstelldatum: new Date().toISOString(),
        trigger_create_card: true,
        brand_create_card: brandId,
      }

      await registerCustomer(registrationData).unwrap()
    } catch (err: unknown) {
      if (
        err &&
        typeof err === 'object' &&
        'data' in err &&
        err.data &&
        typeof err.data === 'object' &&
        'code' in err.data &&
        err.data.code === 'EMAIL_EXISTS'
      ) {
        toast.error(t('auth-errors.email-exists'), {
          duration: 5000,
        })
        return
      }

      const errorMessage =
        err &&
        typeof err === 'object' &&
        'data' in err &&
        err.data &&
        typeof err.data === 'object' &&
        'error' in err.data
          ? String(err.data.error)
          : t('register.messages.registrationFailed')

      toast.error(t('register.messages.registrationError'), {
        description: errorMessage,
        duration: 5000,
      })
    }
  }

  /**
   * Handles marketing terms acceptance toggle.
   * 
   * Updates the marketingAccepted field value without triggering validation
   * or marking the field as dirty to avoid unnecessary form state changes.
   * 
   * @param {boolean} checked - New checkbox state
   */
  const handleTermsToggle = (checked: boolean) => {
    setValue('marketingAccepted', checked, {
      shouldValidate: false,
      shouldDirty: false,
    })
  }

  /**
   * Handles privacy policy acceptance toggle.
   * 
   * Updates the privacyAccepted field value and triggers validation
   * since this is a required field for form submission.
   * 
   * @param {boolean} checked - New checkbox state
   */
  const handlePrivacyToggle = (checked: boolean) => {
    setValue('privacyAccepted', checked, {
      shouldValidate: true,
      shouldDirty: true,
    })
    trigger('privacyAccepted')
  }

  /**
   * Toggles password field visibility.
   */
  const togglePasswordVisibility = () => setShowPassword(!showPassword)
  
  /**
   * Toggles confirm password field visibility.
   */
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword)

  return {
    control,
    handleSubmit,
    onSubmit,
    errors,
    isLoading,
    showSuccessMessage,
    allFieldsEmpty,
    privacyAccepted,
    marketingAccepted,
    showPassword,
    showConfirmPassword,
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    handleTermsToggle,
    handlePrivacyToggle,
    isFormValid,
  }
}
